// Pagali Food › Taxa de entrega — fixa (a do contrato) ou pela distância entre o restaurante e
// o ponto de entrega: base + (km − km incluídos) × preço por km, limitada a [mínimo, máximo];
// acima do raio máximo não se entrega. Valores globais; cada contrato FOOD pode ter excepção.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid2 as Grid, InputAdornment, Paper,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { Refresh, Save, Place } from '@mui/icons-material';
import api from '../../services/api';

interface Tabela {
  modo: 'FIXO' | 'DISTANCIA';
  baseCVE: number; kmIncluidos: number; precoKmCVE: number;
  minimoCVE: number; maximoCVE: number; kmMaximo: number; factorEstrada: number;
}
interface LinhaRestaurante { id: number; nome: string; activo: boolean; temLocalizacao: boolean; tabela: Tabela; excepcao: boolean }

/** A mesma fórmula do servidor (services/food-entrega.service.ts). */
const taxaPorKm = (km: number, t: Tabela) =>
  Math.round(Math.min(Math.max(t.baseCVE + Math.max(0, km - t.kmIncluidos) * t.precoKmCVE, t.minimoCVE), t.maximoCVE));

const CAMPOS: { k: keyof Tabela; rot: string; fim: string; ajuda: string }[] = [
  { k: 'baseCVE', rot: 'Base', fim: 'CVE', ajuda: 'Inclui os primeiros km' },
  { k: 'kmIncluidos', rot: 'Km incluídos na base', fim: 'km', ajuda: 'Até aqui paga só a base' },
  { k: 'precoKmCVE', rot: 'Preço por km a mais', fim: 'CVE', ajuda: 'Por cada km acima dos incluídos' },
  { k: 'minimoCVE', rot: 'Mínimo', fim: 'CVE', ajuda: 'Nenhuma entrega custa menos' },
  { k: 'maximoCVE', rot: 'Máximo', fim: 'CVE', ajuda: 'Nenhuma entrega custa mais' },
  { k: 'kmMaximo', rot: 'Raio máximo', fim: 'km', ajuda: 'Acima disto o restaurante não entrega' },
  { k: 'factorEstrada', rot: 'Factor de estrada', fim: '×', ajuda: 'Linha recta × factor ≈ caminho pelas ruas' },
];

const FoodTaxaEntrega: React.FC = () => {
  const [tabela, setTabela] = useState<Tabela | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [restaurantes, setRestaurantes] = useState<LinhaRestaurante[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState('');
  const [kmSim, setKmSim] = useState('4');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await api.get('/food/admin/entrega');
      const t: Tabela = r.data.data.global;
      setTabela(t);
      setForm(Object.fromEntries(Object.entries(t).map(([k, v]) => [k, String(v)])));
      setRestaurantes(r.data.data.restaurantes || []);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível carregar a taxa de entrega');
    }
    setLoading(false);
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const editada: Tabela | null = useMemo(() => {
    if (!tabela) return null;
    const n = (k: keyof Tabela) => (form[k] === '' || form[k] == null ? (tabela as any)[k] : Number(form[k]));
    return {
      modo: (form.modo as any) || tabela.modo,
      baseCVE: n('baseCVE'), kmIncluidos: n('kmIncluidos'), precoKmCVE: n('precoKmCVE'),
      minimoCVE: n('minimoCVE'), maximoCVE: n('maximoCVE'), kmMaximo: n('kmMaximo'), factorEstrada: n('factorEstrada'),
    };
  }, [form, tabela]);

  const gravar = async () => {
    if (!editada) return;
    setSaving(true);
    setErro(null);
    try {
      await api.put('/food/admin/entrega', editada);
      setOk('Taxa de entrega gravada');
      carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível gravar');
    }
    setSaving(false);
  };

  if (loading || !editada) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  const distancia = editada.modo === 'DISTANCIA';
  const km = Number(kmSim.replace(',', '.')) || 0;
  const exemplos = [1, 2, 3, 5, 8, 12].filter((x) => x <= Math.max(editada.kmMaximo, 1));
  const semLocalizacao = restaurantes.filter((r) => r.activo && !r.temLocalizacao);

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Taxa de entrega</Typography>
          <Typography variant="body2" color="text.secondary">
            Quanto o cliente paga pela entrega. O estafeta recebe sempre o valor fixo do contrato.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Modo</Typography>
          <ToggleButtonGroup exclusive color="primary" value={editada.modo}
            onChange={(_, v) => v && setForm((f) => ({ ...f, modo: v }))}>
            <ToggleButton value="FIXO">Fixa (valor do contrato)</ToggleButton>
            <ToggleButton value="DISTANCIA">Por distância</ToggleButton>
          </ToggleButtonGroup>
          {distancia ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Por distância, o cliente tem de marcar o ponto de entrega no mapa e paga o valor calculado. Ligue só
              quando a versão da app que mostra a taxa calculada no checkout estiver nas lojas — nas versões
              anteriores a app mostra o valor «desde» e o pedido cobra o calculado.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Fixa: cada pedido paga a taxa de entrega do contrato FOOD do restaurante (hoje 250$).
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, opacity: distancia ? 1 : 0.85 }}>
        <CardContent>
          <Typography variant="h6">Tabela por distância (global)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            taxa = base + (km − km incluídos) × preço por km, arredondada ao escudo, entre o mínimo e o máximo.
            Um contrato FOOD pode ter valores próprios (Contratos › Pagali Food).
          </Typography>
          <Grid container spacing={2}>
            {CAMPOS.map((c) => (
              <Grid key={c.k} size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" label={c.rot} value={form[c.k] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.') }))}
                  helperText={c.ajuda}
                  InputProps={{ endAdornment: <InputAdornment position="end">{c.fim}</InputAdornment> }} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <Save />} disabled={saving} onClick={gravar}>
              Guardar
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Simulador</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" label="Distância" value={kmSim} sx={{ width: 160 }}
              onChange={(e) => setKmSim(e.target.value.replace(/[^0-9.,]/g, ''))}
              InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }} />
            <Typography variant="h5" fontWeight={700}>
              {km > editada.kmMaximo ? 'Fora da área de entrega' : `${taxaPorKm(km, editada).toLocaleString('pt-PT')} CVE`}
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow><TableCell>Distância</TableCell><TableCell align="right">Taxa ao cliente</TableCell></TableRow></TableHead>
              <TableBody>
                {exemplos.map((x) => (
                  <TableRow key={x}><TableCell>{x} km</TableCell><TableCell align="right">{taxaPorKm(x, editada).toLocaleString('pt-PT')} CVE</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary">
            Distância pelas ruas ≈ linha recta × {editada.factorEstrada}. Valores com o que está no formulário, antes de guardar.
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>Restaurantes</Typography>
      {semLocalizacao.length > 0 && (
        <Alert severity="info" icon={<Place />} sx={{ mb: 2 }}>
          Sem localização ({semLocalizacao.map((r) => r.nome).join(', ')}): a distância não se calcula e estes
          restaurantes cobram a taxa fixa. Marque a localização em Pagali Food › Restaurantes.
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Restaurante</TableCell>
              <TableCell>Localização</TableCell>
              <TableCell>Modo</TableCell>
              <TableCell>Tabela</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {restaurantes.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell><Typography fontWeight={600}>{r.nome}</Typography>{!r.activo && <Chip size="small" label="Inactivo" sx={{ ml: 1 }} />}</TableCell>
                <TableCell>{r.temLocalizacao ? <Chip size="small" color="success" label="Marcada" /> : <Chip size="small" color="warning" label="Em falta" />}</TableCell>
                <TableCell>{r.tabela.modo === 'DISTANCIA' ? 'Por distância' : 'Fixa'}{r.excepcao && <Chip size="small" label="Contrato" sx={{ ml: 1 }} />}</TableCell>
                <TableCell>
                  {r.tabela.modo === 'DISTANCIA'
                    ? `${r.tabela.baseCVE}$ até ${r.tabela.kmIncluidos} km + ${r.tabela.precoKmCVE}$/km`
                    : 'Valor do contrato'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={!!ok} autoHideDuration={3000} onClose={() => setOk('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success">{ok}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FoodTaxaEntrega;
